import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Typography, Box, Paper, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { buttonStyles } from './common/ButtonStyles';
import { authStyles } from './common/AuthStyles';
import CheckIcon from '@mui/icons-material/Check';
import { alpha } from '@mui/material/styles';
import { color } from '../theme/tokens';

const categories = [
  { value: 'give_suggestions', label: 'Give Suggestions' },
  { value: 'report_issues', label: 'Report Issues' },
  { value: 'request_information', label: 'Request Information' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
];

const ContactUs = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // --- All hooks are now called unconditionally at the top level ---
  const [category, setCategory] = useState('');
  const [feedback, setFeedback] = useState('');
  const [message, setMessage] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const [debugClicks, setDebugClicks] = useState(0);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    // This effect runs for both logged-in and non-logged-in views, which is fine.
    console.log('Debug Info:', {
      userAgent: window.navigator.userAgent,
      platform: window.navigator.platform,
      vendor: window.navigator.vendor
    });
  }, []);

  const handleCopyEmail = () => {
    const user = 'contact';
    const domain = 'my-stack.ai';
    const email = `${user}@${domain}`;
    navigator.clipboard.writeText(email).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Revert back after 2 seconds
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback || !category) {
      setMessage('All fields are required');
      return;
    }

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        await addDoc(collection(db, 'support'), {
          userId: user.uid,
          email: user.email,
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ')[1] || '',
          category,
          feedback,
          timestamp: new Date(),
        });
        setMessage('Your message has been submitted successfully');
        setFeedback('');
        setCategory('');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      setMessage(`Error submitting: ${error.message}`);
    }
  };

  const handleDebugClick = () => {
    const newClicks = debugClicks + 1;
    console.log('Debug clicks:', newClicks);
    setDebugClicks(newClicks);
    if (newClicks >= 3) {
      setShowDebug(true);
      console.log('Debug mode activated');
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      {currentUser ? (
        // --- View for LOGGED-IN users ---
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2, sm: 3 },
            mt: { xs: 2, sm: 8 },
            borderRadius: '1rem',
            border: `0.0625rem solid ${alpha(color.accent, 0.2)}`,
            background: color.paper,
            boxShadow: '0 0.25rem 1.25rem rgba(0,0,0,0.08)'
          }}
        >
          <Typography 
            component="h1" 
            variant="h5" 
            gutterBottom
            onClick={handleDebugClick}
            sx={{
              color: color.evergreen,
              fontWeight: 600,
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              mb: 3,
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            Contact Us
          </Typography>

          {message && (
            <Typography 
              sx={{
                ...authStyles.message,
                color: message.includes('successfully') ? color.accent : color.destructive,
                textAlign: 'center',
                mb: 2
              }}
            >
              {message}
            </Typography>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
                sx={{
                  borderRadius: '0.75rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(color.evergreen, 0.2),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(color.evergreen, 0.3),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: color.accent,
                  }
                }}
              >
                {categories.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="feedback"
              label="Your Feedback"
              name="feedback"
              autoComplete="feedback"
              multiline
              rows={8}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                  '& fieldset': {
                    borderColor: alpha(color.evergreen, 0.2),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(color.evergreen, 0.3),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: color.accent,
                  }
                }
              }}
            />

            <Box sx={{ mt: 3, mb: 2 }}>
              <Button
                type="submit"
                fullWidth
                sx={buttonStyles.auth}
              >
                Submit Feedback
              </Button>
            </Box>
          </Box>

          {/* Debug section */}
          <Box 
            sx={{ 
              mt: 4, 
              p: 2, 
              display: showDebug ? 'block' : 'none',
              backgroundColor: color.surface,
              borderRadius: 1,
              fontSize: '0.75rem',
              wordBreak: 'break-all'
            }}
          >
            <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
              Debug Information (Click Count: {debugClicks})
            </Typography>
            <pre style={{ whiteSpace: 'pre-wrap' }}>
              UserAgent: {window.navigator.userAgent}
              {'\n'}
              Platform: {window.navigator.platform}
              {'\n'}
              Vendor: {window.navigator.vendor}
              {'\n'}
              Mobile: {/Mobile|Android|iPhone/i.test(window.navigator.userAgent).toString()}
              {'\n'}
              Screen: {window.innerWidth}x{window.innerHeight}
            </pre>
          </Box>
        </Paper>
      ) : (
        // --- View for NON-LOGGED-IN users ---
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2, sm: 3 },
            mt: { xs: 2, sm: 8 },
            borderRadius: '1rem',
            border: `0.0625rem solid ${alpha(color.accent, 0.2)}`,
            background: color.paper,
            boxShadow: '0 0.25rem 1.25rem rgba(0,0,0,0.08)',
            textAlign: 'center'
          }}
        >
          <Typography 
            component="h1" 
            variant="h5" 
            gutterBottom
            sx={{
              color: color.evergreen,
              fontWeight: 600,
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              mb: 2,
            }}
          >
            Contact Us
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            For general inquiries, suggestions, or partnership opportunities, please feel free to reach out to us directly.
          </Typography>
          <Button
            onClick={handleCopyEmail}
            sx={{
              ...buttonStyles.auth,
              backgroundColor: isCopied ? color.accent : buttonStyles.auth.backgroundColor,
              '&:hover': {
                backgroundColor: isCopied ? color.accentHover : buttonStyles.auth['&:hover'].backgroundColor,
              }
            }}
            startIcon={isCopied ? <CheckIcon /> : null}
          >
            {isCopied ? 'Email Copied!' : 'Copy Email Address'}
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default ContactUs;